import { z } from 'zod';
import { ACCU_GROWTH_RATES, ACCU_MAX_CASHOUT_TICKS, accuMultiplier, RUSH_GROWTH_RATE, type AccuGrowthRate } from '../accumulator';
import { MODALITY_SPECS, type Direction, type Modality } from '../contracts';
import { tickMoves } from '../digits';
import type { MarketWindow, Strategy } from './types';

/**
 * User-built strategies (paid feature). The definition is plain JSON stored in
 * AutomationConfiguration.strategyParams; this one interpreter runs it both in
 * the worker and in the browser backtest, so a test reads exactly like a session.
 *
 * Rules decide WHEN and WHICH side, first enabled match wins. Around them:
 * loss virtual (the signal must miss N times on paper before a real entry),
 * gale side, waits after a won/lost chain, an operating window (Brasília time)
 * and chain-count stops. Stakes, gales count, meta and limit stay in the config.
 */

export const CUSTOM_STRATEGY_KEY = 'custom';

/** "Meus robôs" slots per user (robots already saved beyond it are kept; only new ones are refused). */
export const CUSTOM_ROBOT_SLOTS = 2;

export const CUSTOM_LIMITS = {
  rules: 12,
  pattern: 12,
  /**
   * A pattern drawn on the Fixa board (Testar padrão › Na Fixa, the robot's "Na Fixa" trigger): first painted round to
   * the entry, gaps and skip included. A live robot reads the last 160 rounds, so 64 always fit, Empates left out.
   */
  boardReach: 64,
  streak: { min: 2, max: 15 },
  alternation: { min: 3, max: 12 },
  breakRun: { min: 2, max: 15 },
  frequency: { min: 5, max: 50 },
  /** "Equilíbrio" trigger: the windows of the Análise page's Equilíbrio and the share a side must reach (5% steps). */
  balance: { windows: [20, 50, 100], pct: { min: 55, max: 95 } },
  digitRepeat: { min: 1, max: 5 },
  numberRepeat: { min: 2, max: 5 },
  virtualLoss: 10,
  waitAfterWin: 30,
  waitAfterLoss: 60,
  stopLossStreak: 20,
  stopWins: 200,
  /** Gales a gale plan can fix (the panel goes up to 10). */
  galePlan: 10,
} as const;

const side = z.enum(['EVEN', 'ODD', 'RISE', 'FALL']);
export type Side = z.infer<typeof side>;
/**
 * Rush only: a candle by the profit its round reached before falling — at least `pct`% (GTE) or
 * below it (LT), read at the Rush rate exactly like the round history chips.
 */
const candleToken = z.object({ op: z.enum(['GTE', 'LT']), pct: z.number().int().min(1).max(5000) });
export type CandleToken = z.infer<typeof candleToken>;
/** One round of a pattern: a side, any result, (Dígitos only) the exact closing digit 0–9, or (Rush only) a candle. */
const patternToken = z.union([side, z.literal('ANY'), z.number().int().min(0).max(9), candleToken]);
export type PatternToken = z.infer<typeof patternToken>;

export const isCandle = (x: PatternToken): x is CandleToken => typeof x === 'object' && x !== null;

/**
 * Rush strategies read rounds by the color of their candle, the same bands as the round history:
 * cinza (até +99%) = FALL, verde (+100% ou mais, dourada included) = RISE; a pattern can also ask for
 * a dourada (+1.000% ou mais). The cash-out is NOT part of the strategy: it only decides whether an
 * entry won (the round flew to it).
 */
export const RUSH_GREEN_PCT = 100;
export const RUSH_GOLD_PCT = 1000;
export const RUSH_GOLD: CandleToken = { op: 'GTE', pct: RUSH_GOLD_PCT };
export const isRushGold = (x: PatternToken) => isCandle(x) && x.op === 'GTE' && x.pct === RUSH_GOLD_PCT;

/** Side of a Rush round by its candle: verde (or dourada) RISE, cinza FALL. */
export function rushSide(ticks: number): Side {
  return flightPct(ticks) + 1e-9 >= RUSH_GREEN_PCT ? 'RISE' : 'FALL';
}

/** Profit (%) a Rush round reached after flying `ticks` ticks — the value on its history chip. */
export function flightPct(ticks: number): number {
  return (accuMultiplier(RUSH_GROWTH_RATE, ticks) - 1) * 100;
}

export function candleMatches(c: CandleToken, ticks: number): boolean {
  // Same float tolerance as formatProfitPct: a chip reading +50% counts as reaching 50%.
  const reached = flightPct(ticks) + 1e-9 >= c.pct;
  return c.op === 'GTE' ? reached : !reached;
}

/** "laranja" (the 1.000%+ candle) / "+50% ou mais" / "abaixo de +20%" */
export function candleLabel(c: CandleToken): string {
  if (c.op === 'GTE' && c.pct === RUSH_GOLD_PCT) return 'laranja';
  if (c.pct === RUSH_GREEN_PCT) return c.op === 'GTE' ? 'verde' : 'cinza';
  return c.op === 'GTE' ? `+${c.pct.toLocaleString('pt-BR')}% ou mais` : `abaixo de +${c.pct.toLocaleString('pt-BR')}%`;
}
/**
 * `and` (2026-11-01): joined with the trigger above ("e"): a group of joined triggers opens a cycle only when every
 * enabled one matches on the same round, on the side of its first. Absent = "ou", as before. Ignored on the first.
 */
const ruleBase = { id: z.string().min(1).max(16), enabled: z.boolean().default(true), and: z.boolean().optional() };
/**
 * How a pattern was drawn on the Fixa board, to draw it back the same way: rounds per row and the board cell of its
 * first round. It changes nothing in what the pattern matches.
 */
const boardSchema = z.object({ perRow: z.number().int().min(4).max(30), start: z.number().int().min(0).max(255) });
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Horário inválido');

export const customRuleSchema = z.discriminatedUnion('type', [
  /** Exact sequence of the latest rounds (oldest → newest); ANY matches anything. */
  z.object({ ...ruleBase, type: z.literal('PATTERN'), sequence: z.array(patternToken).min(1).max(CUSTOM_LIMITS.boardReach), entry: side, board: boardSchema.optional() }),
  /** N rounds of the same side in a row. */
  z.object({
    ...ruleBase,
    type: z.literal('STREAK'),
    length: z.number().int().min(CUSTOM_LIMITS.streak.min).max(CUSTOM_LIMITS.streak.max),
    exact: z.boolean().default(true),
    entry: z.enum(['WITH', 'AGAINST']),
  }),
  /** N rounds alternating (A B A B…). */
  z.object({
    ...ruleBase,
    type: z.literal('ALTERNATION'),
    length: z.number().int().min(CUSTOM_LIMITS.alternation.min).max(CUSTOM_LIMITS.alternation.max),
    entry: z.enum(['CONTINUE', 'BREAK']),
  }),
  /** A run of N+ of one side, then the newest round flipped. */
  z.object({
    ...ruleBase,
    type: z.literal('BREAK'),
    minRun: z.number().int().min(CUSTOM_LIMITS.breakRun.min).max(CUSTOM_LIMITS.breakRun.max),
    entry: z.enum(['FOLLOW', 'RETURN']),
  }),
  /** One side appeared at least `min` times in the last `window` rounds. */
  z.object({
    ...ruleBase,
    type: z.literal('FREQUENCY'),
    window: z.number().int().min(CUSTOM_LIMITS.frequency.min).max(CUSTOM_LIMITS.frequency.max),
    min: z.number().int().min(3).max(CUSTOM_LIMITS.frequency.max),
    entry: z.enum(['MAJORITY', 'MINORITY']),
  }),
  /**
   * "Equilíbrio" (as on the Análise page): a side — `watch`, or either one — closed `pct`% or more of the last `window`
   * rounds, Empates left out. Enters on that side (MAJORITY) or on the other one (MINORITY), every round it holds.
   */
  z.object({
    ...ruleBase,
    type: z.literal('BALANCE'),
    window: z.union([z.literal(20), z.literal(50), z.literal(100)]),
    watch: z.enum(['ANY', 'EVEN', 'ODD', 'RISE', 'FALL']),
    pct: z.number().int().min(CUSTOM_LIMITS.balance.pct.min).max(CUSTOM_LIMITS.balance.pct.max).multipleOf(5),
    entry: z.enum(['MAJORITY', 'MINORITY']),
  }),
  /** Dígitos only: the same digit closed `count` rounds in a row (e.g. 7 7); enters by that digit's parity. */
  z.object({
    ...ruleBase,
    type: z.literal('REPEAT'),
    count: z.number().int().min(CUSTOM_LIMITS.numberRepeat.min).max(CUSTOM_LIMITS.numberRepeat.max),
    entry: z.enum(['WITH', 'AGAINST']),
  }),
  /** Dígitos only: the last `repeat` closing digits are all in `digits`. */
  z.object({
    ...ruleBase,
    type: z.literal('DIGIT'),
    digits: z.array(z.number().int().min(0).max(9)).min(1).max(9),
    repeat: z.number().int().min(CUSTOM_LIMITS.digitRepeat.min).max(CUSTOM_LIMITS.digitRepeat.max),
    entry: side,
  }),
]);
export type CustomRule = z.infer<typeof customRuleSchema>;
export type CustomRuleType = CustomRule['type'];

export const customStrategySchema = z.object({
  v: z.literal(1).default(1),
  rules: z.array(customRuleSchema).min(1).max(CUSTOM_LIMITS.rules),
  /** Loss virtual: real entries only after the signal missed `count` times in a row on paper (0 = off). */
  virtualLoss: z.object({ count: z.number().int().min(0).max(CUSTOM_LIMITS.virtualLoss) }).default({ count: 0 }),
  /**
   * Side of each gale: repeat the last entry, invert it, or read the rules again (keeps the side when none matches).
   * `plan` (optional) fixes the side of gale 1, 2, 3… against the cycle's first entry: ENTRY = that side,
   * OPPOSITE = the other one; gales past the plan follow `direction`.
   */
  gale: z
    .object({ direction: z.enum(['SAME', 'INVERT', 'SIGNAL', 'FOLLOW']), plan: z.array(z.enum(['ENTRY', 'OPPOSITE'])).min(1).max(CUSTOM_LIMITS.galePlan).optional() })
    .default({ direction: 'SAME' }),
  /** Rounds to sit out after a won / lost chain. */
  afterWin: z.object({ waitRounds: z.number().int().min(0).max(CUSTOM_LIMITS.waitAfterWin) }).default({ waitRounds: 0 }),
  afterLoss: z.object({ waitRounds: z.number().int().min(0).max(CUSTOM_LIMITS.waitAfterLoss) }).default({ waitRounds: 0 }),
  /** End the session after N lost chains in a row / N won chains (0 = off). */
  stops: z
    .object({
      lossStreak: z.number().int().min(0).max(CUSTOM_LIMITS.stopLossStreak),
      wins: z.number().int().min(0).max(CUSTOM_LIMITS.stopWins),
    })
    .default({ lossStreak: 0, wins: 0 }),
  /** New chains only start inside this window, Brasília time (a chain already running finishes). */
  schedule: z.object({ enabled: z.boolean(), from: hhmm, to: hhmm }).default({ enabled: false, from: '09:00', to: '18:00' }),
  /**
   * Decolagem only: growth per tick and the cash-out after N ticks inside the corridor. A round that
   * flew at least N ticks is "Longo" (the bet would have cashed out), else "Curto".
   */
  accu: z
    .object({
      growthRate: z.number().refine((v): v is AccuGrowthRate => (ACCU_GROWTH_RATES as readonly number[]).includes(v), 'Crescimento deve ser de 1% a 5%'),
      cashoutTicks: z.number().int().min(1).max(ACCU_MAX_CASHOUT_TICKS),
    })
    .optional(),
});
export type CustomStrategyDef = z.infer<typeof customStrategySchema>;

// ---------------------------------------------------------------------------

export function sidesOf(modality: Modality): readonly [Side, Side] {
  // Rush rounds read by candle color: verde (RISE) / cinza (FALL).
  if (modality === 'ACCUMULATOR') return ['RISE', 'FALL'];
  const [a, b = a] = MODALITY_SPECS[modality].directions;
  return [a.direction as Side, b.direction as Side];
}

export function sideLabel(modality: Modality, s: Direction): string {
  if (modality === 'ACCUMULATOR') return s === 'RISE' ? 'verde' : s === 'FALL' ? 'cinza' : s;
  return MODALITY_SPECS[modality].directions.find((d) => d.direction === s)?.label ?? s;
}

function other(modality: Modality, s: Direction): Side {
  const [a, b] = sidesOf(modality);
  return s === a ? b : a;
}

/**
 * Closing digits of the window, newest first; on Dígitos an Empate (the round closed level: Polarium
 * returned every entry) reads as -1 — no number and no parity, as the game shows it (owner 2026-10-23).
 */
export function windowDigits(modality: Modality, window: MarketWindow): number[] {
  const moves = window.moves;
  if (modality !== 'EVEN_ODD' || !moves) return [...window.digits];
  return window.digits.map((d, i) => (moves[i] === 'FLAT' ? -1 : d));
}

/** Side of each finished round, newest first (null = Empate: a level close returns the entry, on every game). */
export function windowSides(modality: Modality, window: MarketWindow): (Side | null)[] {
  if (modality === 'EVEN_ODD') return windowDigits(modality, window).map((d) => (d < 0 ? null : d % 2 === 0 ? 'EVEN' : 'ODD'));
  if (modality === 'ACCUMULATOR' && window.flights) return window.flights.map(rushSide);
  const moves = window.moves ?? tickMoves(window.quotes);
  return moves.map((m) => (m === 'UP' ? 'RISE' : m === 'DOWN' ? 'FALL' : null));
}

/**
 * The window the rules read, newest first: Empates left out — an Empate is neutral, so a run goes on across it
 * (5 Par, Empate, 5 Par = 10 Par; owner 2026-10-23, the same as the Análise). Sides, digits and Rush flights stay aligned.
 */
export function ruleWindow(
  modality: Modality,
  window: MarketWindow,
  sides: readonly (Side | null)[] = windowSides(modality, window),
): { sides: readonly Side[]; digits: readonly number[]; flights?: readonly number[] } {
  // The usual case, no Empate in the window: read as it is (the backtest calls this every round).
  if (!sides.includes(null)) return { sides: sides as Side[], digits: window.digits, ...(window.flights ? { flights: window.flights } : {}) };
  const digits = windowDigits(modality, window);
  const keep = sides.map((s, i) => (s === null ? -1 : i)).filter((i) => i >= 0);
  return {
    sides: keep.map((i) => sides[i]!),
    digits: keep.map((i) => digits[i]!),
    ...(window.flights ? { flights: keep.map((i) => window.flights![i]!) } : {}),
  };
}

/** Most rounds any rule needs to look back. */
export function lookbackOf(def: CustomStrategyDef): number {
  let n = 1;
  for (const r of def.rules) {
    if (r.type === 'PATTERN') n = Math.max(n, r.sequence.length);
    else if (r.type === 'STREAK') n = Math.max(n, r.length + 1);
    else if (r.type === 'ALTERNATION') n = Math.max(n, r.length);
    else if (r.type === 'BREAK') n = Math.max(n, r.minRun + 1);
    else if (r.type === 'FREQUENCY' || r.type === 'BALANCE') n = Math.max(n, r.window);
    else if (r.type === 'REPEAT') n = Math.max(n, r.count);
    else n = Math.max(n, r.repeat);
  }
  return n;
}

/** Rounds of its window a side needs for an "Equilíbrio" trigger: 70% of 20 = 14. */
export const balanceNeed = (rule: { window: number; pct: number }) => Math.ceil((rule.pct * rule.window) / 100);

/** The side a rule enters on the given window, or null when it doesn't match. */
export function matchRule(rule: CustomRule, modality: Modality, sides: readonly (Side | null)[], digits: readonly number[], flights: readonly number[] = []): Side | null {
  const s0 = sides[0];
  switch (rule.type) {
    case 'PATTERN': {
      const n = rule.sequence.length;
      if (sides.length < n) return null;
      // sequence is oldest → newest; sides is newest first.
      for (let i = 0; i < n; i++) {
        const want = rule.sequence[n - 1 - i]!;
        if (want === 'ANY') continue;
        if (isCandle(want)) {
          const ticks = flights[i];
          if (modality !== 'ACCUMULATOR' || ticks === undefined || !candleMatches(want, ticks)) return null;
        } else if (typeof want === 'number') {
          if (modality !== 'EVEN_ODD' || digits[i] !== want) return null;
        } else if (sides[i] !== want) return null;
      }
      return rule.entry;
    }
    case 'STREAK': {
      if (!s0 || sides.length < rule.length) return null;
      for (let i = 1; i < rule.length; i++) if (sides[i] !== s0) return null;
      if (rule.exact && sides[rule.length] === s0) return null;
      return rule.entry === 'WITH' ? s0 : other(modality, s0);
    }
    case 'ALTERNATION': {
      if (!s0 || sides.length < rule.length) return null;
      for (let i = 1; i < rule.length; i++) if (!sides[i] || sides[i] === sides[i - 1]) return null;
      return rule.entry === 'CONTINUE' ? other(modality, s0) : s0;
    }
    case 'BREAK': {
      const s1 = sides[1];
      if (!s0 || !s1 || s0 === s1) return null;
      let run = 1;
      while (run + 1 < sides.length && sides[run + 1] === s1) run++;
      if (run < rule.minRun) return null;
      return rule.entry === 'FOLLOW' ? s0 : s1;
    }
    case 'FREQUENCY': {
      if (sides.length < rule.window) return null;
      const [a, b] = sidesOf(modality);
      let na = 0;
      let nb = 0;
      for (let i = 0; i < rule.window; i++) {
        if (sides[i] === a) na++;
        else if (sides[i] === b) nb++;
      }
      const top = na >= rule.min ? a : nb >= rule.min ? b : null;
      if (!top) return null;
      return rule.entry === 'MAJORITY' ? top : other(modality, top);
    }
    case 'BALANCE': {
      if (modality === 'ACCUMULATOR' || sides.length < rule.window) return null;
      const [a, b] = sidesOf(modality);
      let na = 0;
      let nb = 0;
      for (let i = 0; i < rule.window; i++) {
        if (sides[i] === a) na++;
        else if (sides[i] === b) nb++;
      }
      // Over half (55%+): only one side can be there.
      const need = balanceNeed(rule);
      const top = na >= need ? a : nb >= need ? b : null;
      if (!top || (rule.watch !== 'ANY' && rule.watch !== top)) return null;
      return rule.entry === 'MAJORITY' ? top : other(modality, top);
    }
    case 'REPEAT': {
      if (modality !== 'EVEN_ODD' || digits.length < rule.count) return null;
      const d = digits[0]!;
      if (d < 0) return null;
      for (let i = 1; i < rule.count; i++) if (digits[i] !== d) return null;
      const parity: Side = d % 2 === 0 ? 'EVEN' : 'ODD';
      return rule.entry === 'WITH' ? parity : other(modality, parity);
    }
    case 'DIGIT': {
      if (modality !== 'EVEN_ODD' || digits.length < rule.repeat) return null;
      for (let i = 0; i < rule.repeat; i++) if (!rule.digits.includes(digits[i]!)) return null;
      return rule.entry;
    }
  }
}

// ---- Human text (builder cards, decision log) -------------------------------

const ENTRY_TEXT = {
  STREAK: { WITH: 'a favor', AGAINST: 'contra' },
  ALTERNATION: { CONTINUE: 'segue alternando', BREAK: 'entra na quebra' },
  BREAK: { FOLLOW: 'segue a quebra', RETURN: 'volta ao lado da sequência' },
  FREQUENCY: { MAJORITY: 'lado que mais saiu', MINORITY: 'lado que menos saiu' },
  REPEAT: { WITH: 'mesma paridade', AGAINST: 'paridade oposta' },
} as const;

export const RULE_TYPE_LABEL: Record<CustomRuleType, string> = {
  PATTERN: 'Padrão personalizado',
  STREAK: 'Sequência',
  ALTERNATION: 'Alternância',
  BREAK: 'Quebra de sequência',
  FREQUENCY: 'Frequência',
  BALANCE: 'Equilíbrio',
  DIGIT: 'Dígito',
  REPEAT: 'Número repetido',
};

const tokenText = (modality: Modality) => (x: PatternToken) => (x === 'ANY' ? '?' : typeof x === 'number' ? String(x) : isCandle(x) ? candleLabel(x) : sideLabel(modality, x));

/** "Sequência de 3 iguais → contra" style one-liner. */
export function describeRule(rule: CustomRule, modality: Modality): string {
  const L = (s: Direction) => sideLabel(modality, s);
  // Decolagem: a trigger only says when to bet (there is no side to pick).
  if (modality === 'ACCUMULATOR') {
    switch (rule.type) {
      case 'PATTERN':
        return `${rule.sequence.map(tokenText(modality)).join(', ')} → entrada`;
      case 'STREAK':
        return `${rule.exact ? '' : 'Pelo menos '}${rule.length} velas da mesma cor seguidas → entrada`;
      case 'ALTERNATION':
        return `${rule.length} velas alternando cinza / verde → entrada`;
      case 'BREAK':
        return `Cor mudou após ${rule.minRun}+ velas iguais → entrada`;
      case 'FREQUENCY':
        return `Mesma cor em ${rule.min}+ das últimas ${rule.window} velas → entrada`;
      default:
        return `${RULE_TYPE_LABEL[rule.type]} → entrada`;
    }
  }
  switch (rule.type) {
    case 'PATTERN':
      if (rule.board) return `Fixa: ${boardTriggerText(rule.sequence, modality)} → ${L(rule.entry)}`;
      return `${rule.sequence.map(tokenText(modality)).join(' ')} → ${L(rule.entry)}`;
    case 'STREAK':
      return `${rule.exact ? 'Exatamente ' : 'Pelo menos '}${rule.length} iguais seguidos → ${ENTRY_TEXT.STREAK[rule.entry]}`;
    case 'ALTERNATION':
      return `${rule.length} alternados → ${ENTRY_TEXT.ALTERNATION[rule.entry]}`;
    case 'BREAK':
      return `Quebra após ${rule.minRun}+ iguais → ${ENTRY_TEXT.BREAK[rule.entry]}`;
    case 'FREQUENCY':
      return `${rule.min}+ de ${rule.window} de um lado → ${ENTRY_TEXT.FREQUENCY[rule.entry]}`;
    case 'BALANCE': {
      const who = rule.watch === 'ANY' ? 'Um lado' : L(rule.watch);
      const to = rule.watch === 'ANY' ? (rule.entry === 'MAJORITY' ? 'lado acima' : 'lado abaixo') : L(rule.entry === 'MAJORITY' ? rule.watch : other(modality, rule.watch));
      return `${who} em ${rule.pct}%+ das últimas ${rule.window} → ${to}`;
    }
    case 'REPEAT':
      return `Mesmo número ${rule.count}× seguidas → ${ENTRY_TEXT.REPEAT[rule.entry]}`;
    case 'DIGIT':
      return `${rule.repeat > 1 ? `${rule.repeat}× seguidas ` : ''}dígito ${rule.digits.join(', ')} → ${L(rule.entry)}`;
  }
}

/**
 * A Fixa pattern in words, runs of "qualquer" counted: "Par Par · 12 qualquer · Par Par · pula 13" (the trailing
 * "qualquer" are the rounds skipped before the entry).
 */
function boardTriggerText(seq: readonly PatternToken[], modality: Modality): string {
  let end = seq.length;
  while (end > 0 && seq[end - 1] === 'ANY') end--;
  // A trigger with every round erased (the builder keeps it as one "qualquer" until something is painted).
  if (!end) return 'nada pintado';
  const parts: string[] = [];
  for (let i = 0; i < end; ) {
    let j = i;
    if (seq[i] === 'ANY') {
      while (seq[j] === 'ANY') j++;
      parts.push(`${j - i} qualquer`);
    } else {
      const run: string[] = [];
      while (j < end && seq[j] !== 'ANY') run.push(tokenText(modality)(seq[j++]!));
      parts.push(run.join(' '));
    }
    i = j;
  }
  const skip = seq.length - end;
  // A scattered drawing would make a paragraph (decision log, robot status): told by its size instead.
  const painted = seq.slice(0, end).filter((x) => x !== 'ANY').length;
  const body = parts.length > 5 ? [`${painted} rodadas em ${end} casas`] : parts;
  return [...body, ...(skip ? [`pula ${skip}`] : [])].join(' · ');
}

const GALE_MODE_TEXT = { SAME: 'gale mantém o lado', INVERT: 'gale troca de lado', SIGNAL: 'gale segue o sinal', FOLLOW: 'gale no lado que saiu' } as const;

/**
 * The gale side in words: "gale mantém o lado", or a plan — with the sides when every trigger enters on the
 * same one ("gales: Ímpar, Par, Par…"), else against the entry ("gales: oposto, entrada…").
 */
export function galeText(def: CustomStrategyDef, modality: Modality): string {
  const plan = def.gale.plan;
  if (!plan?.length) return GALE_MODE_TEXT[def.gale.direction];
  // The side of each group is its first trigger's.
  const entries = new Set(ruleGroups(def.rules).map(([r]) => (r!.type === 'PATTERN' || r!.type === 'DIGIT' ? r!.entry : null)));
  const [x] = entries;
  const steps =
    entries.size === 1 && x
      ? plan.map((p) => sideLabel(modality, p === 'ENTRY' ? x : other(modality, x)))
      : plan.map((p) => (p === 'ENTRY' ? 'entrada' : 'oposto'));
  return `gales: ${steps.join(', ')}`;
}

/** Errors the schema can't see: sides of another modality, impossible thresholds. */
export function validateCustomDef(def: CustomStrategyDef, modality: Modality): string | null {
  if (modality !== 'EVEN_ODD' && modality !== 'RISE_FALL' && modality !== 'ACCUMULATOR') return 'Modalidade não disponível para estratégias.';
  if (modality === 'ACCUMULATOR' && !def.accu) return 'Escolha o saque do Rush.';
  const valid = new Set<string>(sidesOf(modality));
  if (!def.rules.some((r) => r.enabled)) return 'Ative pelo menos um gatilho.';
  if (!ruleGroups(def.rules).length) return 'Ative o primeiro gatilho do grupo: os que estão junto com ele no "e" só confirmam.';
  for (const [i, r] of def.rules.entries()) {
    const n = `Gatilho ${i + 1}`;
    if (r.type === 'PATTERN' || r.type === 'DIGIT') {
      if (!valid.has(r.entry)) return `${n}: entrada inválida para a modalidade.`;
    }
    if (r.type === 'PATTERN') {
      if (r.sequence.some((x) => typeof x === 'number') && modality !== 'EVEN_ODD') return `${n}: números só existem em Dígitos.`;
      if (r.sequence.some(isCandle) && modality !== 'ACCUMULATOR') return `${n}: velas em % só existem no Rush.`;
      if (r.sequence.some((x) => typeof x === 'string' && x !== 'ANY' && !valid.has(x))) return `${n}: padrão com lado inválido para a modalidade.`;
      if (r.sequence.every((x) => x === 'ANY')) return `${n}: o padrão precisa de pelo menos uma rodada definida.`;
      // Only a pattern drawn on the Fixa goes past the hand-built limit.
      if (!r.board && r.sequence.length > CUSTOM_LIMITS.pattern) return `${n}: no máximo ${CUSTOM_LIMITS.pattern} rodadas no padrão.`;
      if (r.board && modality === 'ACCUMULATOR') return `${n}: a Fixa só existe em Dígitos e Sobe e Desce.`;
    }
    if (r.type === 'BALANCE' && modality === 'ACCUMULATOR') return `${n}: o equilíbrio só existe em Dígitos e Sobe e Desce.`;
    if (r.type === 'BALANCE' && r.watch !== 'ANY' && !valid.has(r.watch)) return `${n}: lado inválido para a modalidade.`;
    if (r.type === 'FREQUENCY' && (r.min > r.window || r.min * 2 <= r.window)) return `${n}: o mínimo deve ser mais da metade da janela e no máximo a janela.`;
    if (r.type === 'DIGIT' && modality !== 'EVEN_ODD') return `${n}: regra de dígito só existe em Dígitos.`;
    if (r.type === 'REPEAT' && modality !== 'EVEN_ODD') return `${n}: número repetido só existe em Dígitos.`;
    if (r.type === 'DIGIT' && new Set(r.digits).size !== r.digits.length) return `${n}: dígitos repetidos.`;
  }
  if (new Set(def.rules.map((r) => r.id)).size !== def.rules.length) return 'Gatilhos com identificador repetido.';
  if (def.schedule.enabled && def.schedule.from === def.schedule.to) return 'O horário de início e fim não podem ser iguais.';
  return null;
}

// ---- Interpreter ------------------------------------------------------------

export interface CustomState {
  /** Side of the last real entry (gales read it). */
  last: Side | null;
  /** The last real entry came back (Empate): "no lado que saiu" repeats its side, nothing came out. */
  lastTied?: boolean;
  /** Side of the cycle's first real entry (a gale plan reads it). */
  entry: Side | null;
  /** Paper entry waiting for its round to close. */
  vEntry: { round: number; direction: Side } | null;
  /** Paper misses in a row. */
  vLosses: number;
  /** Rounds to sit out, set when a chain ends; turned into resumeAt on the next read. */
  wait: number;
  resumeAt: number | null;
  /** Lost chains in a row / won chains since the start. */
  lossStreak: number;
  wins: number;
  /** What the robot is doing now, for the user's screen. */
  note: string;
}

const INITIAL_STATE: CustomState = { last: null, entry: null, vEntry: null, vLosses: 0, wait: 0, resumeAt: null, lossStreak: 0, wins: 0, note: 'Lendo o mercado' };

/** Minutes since midnight in Brasília (UTC−3, no daylight saving since 2019). */
export function brasiliaMinutes(epochSeconds: number): number {
  return (((Math.floor(epochSeconds / 60) - 180) % 1440) + 1440) % 1440;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h! * 60 + m!;
}

export function insideSchedule(schedule: CustomStrategyDef['schedule'], epochSeconds: number): boolean {
  if (!schedule.enabled) return true;
  const now = brasiliaMinutes(epochSeconds);
  const from = toMinutes(schedule.from);
  const to = toMinutes(schedule.to);
  return from < to ? now >= from && now < to : now >= from || now < to;
}

/**
 * The newest part of the window the rules can read: `need` rounds with a side and the Empates among them (at least the
 * last 10, where a paper entry settles). The live window holds 160 rounds; reading only this keeps a long backtest fast.
 */
function recentWindow(window: MarketWindow, need: number): MarketWindow {
  const moves = window.moves;
  if (!moves) return window;
  let len = 0;
  for (let sided = 0; len < moves.length && (sided < need || len < 10); len++) if (moves[len] !== 'FLAT') sided++;
  if (len >= moves.length) return window;
  return {
    digits: window.digits.slice(0, len),
    quotes: window.quotes.slice(0, len),
    moves: moves.slice(0, len),
    ...(window.roundIds ? { roundIds: window.roundIds.slice(0, len) } : {}),
    ...(window.flights ? { flights: window.flights.slice(0, len) } : {}),
  };
}

/**
 * The triggers as the robot reads them: groups joined with "e" (each trigger with `and` joins the one above), the
 * enabled ones only, in priority order. Any group opens a cycle ("ou"); inside one, all must match.
 * A group whose first trigger is off is off: the others only confirm, and their own side isn't even shown.
 */
export function ruleGroups<R extends CustomRule>(rules: readonly R[]): R[][] {
  const groups: R[][] = [];
  let cur: R[] = [];
  for (const [i, r] of rules.entries()) {
    if (i === 0 || !r.and) groups.push((cur = []));
    if (r.enabled && (cur.length > 0 || i === 0 || !r.and)) cur.push(r);
  }
  return groups.filter((g) => g.length > 0);
}

/** A trigger joined with "e" only confirms: what it waits for, without the side (its group's first picks it). */
export function describeCondition(rule: CustomRule, modality: Modality): string {
  const text = describeRule(rule, modality);
  const at = text.lastIndexOf(' → ');
  return at > 0 ? text.slice(0, at) : text;
}

/** One group in words: its first trigger, then what the others confirm ("A → Par e B"). */
export function describeGroup(group: readonly CustomRule[], modality: Modality): string {
  return group.map((r, i) => (i === 0 ? describeRule(r, modality) : describeCondition(r, modality))).join(' e ');
}

/** The triggers in words: "A e B ou C". */
export function describeRules(rules: readonly CustomRule[], modality: Modality): string {
  return ruleGroups(rules)
    .map((g) => describeGroup(g, modality))
    .join(' ou ');
}

/** First group whose triggers all match, on the side of its first (its other triggers only confirm). */
function firstMatch(def: CustomStrategyDef, modality: Modality, sides: readonly (Side | null)[], digits: readonly number[], flights?: readonly number[]) {
  for (const group of ruleGroups(def.rules)) {
    const direction = matchRule(group[0]!, modality, sides, digits, flights);
    if (!direction) continue;
    if (group.every((r, i) => i === 0 || matchRule(r, modality, sides, digits, flights))) return { direction, rule: group[0]!, rules: group };
  }
  return null;
}

export const customStrategy: Strategy<CustomStrategyDef, CustomState> = {
  key: CUSTOM_STRATEGY_KEY,
  name: 'Estratégia personalizada',
  description: 'Regras de entrada, loss virtual, gales e pausas definidos pelo usuário.',
  modalities: ['EVEN_ODD', 'RISE_FALL', 'ACCUMULATOR'],
  paramsSchema: customStrategySchema as unknown as z.ZodType<CustomStrategyDef>,
  defaultParams: (m) => customStrategySchema.parse({ rules: [{ id: 'r1', type: 'STREAK', length: 3, exact: true, entry: 'AGAINST' }] }),
  initialState: () => ({ ...INITIAL_STATE }),
  validate: validateCustomDef,

  onTick: (ctx, tick) => {
    const def = ctx.params;
    const st: CustomState = { ...INITIAL_STATE, ...(ctx.state as Partial<CustomState>) };
    const window = recentWindow(ctx.window, lookbackOf(def));
    const sides = windowSides(ctx.modality, window);
    const ids = window.roundIds ?? [];
    const level = ctx.level ?? 0;
    const round = ctx.roundId;
    const L = (s: Direction) => sideLabel(ctx.modality, s);
    const idle = (note: string) => ({ intent: null, state: { ...st, note }, reason: note });

    // 1. Settle the paper entry once its round is in the window.
    if (st.vEntry) {
      const i = ids.indexOf(st.vEntry.round);
      if (i >= 0) {
        // Rush: the paper entry won when that round flew to the robot's cash-out (not by its color).
        const flown = window.flights?.[i];
        const rush = ctx.modality === 'ACCUMULATOR' && def.accu && flown !== undefined;
        // Empate: like a real first entry that came back — neither a miss nor a hit; the robot reads the market again.
        if (rush || sides[i] !== null) {
          const won = rush ? flown >= def.accu!.cashoutTicks : sides[i] === st.vEntry.direction;
          st.vLosses = won ? 0 : st.vLosses + 1;
        }
        st.vEntry = null;
      } else if (round === undefined || round - st.vEntry.round > 3) {
        st.vEntry = null; // that round never reached us: don't count it either way
      }
    }

    const read = ruleWindow(ctx.modality, window, sides);
    const found = firstMatch(def, ctx.modality, read.sides, read.digits, read.flights);
    // Decolagem: a trigger means "bet this round", a bet that wins when the flight is Longo (RISE):
    // that is also what its paper entry is checked against.
    const match = found && ctx.modality === 'ACCUMULATOR' ? { ...found, direction: 'RISE' as Side } : found;

    // 2. Inside a chain, gales go out every round, whatever the filters say.
    if (level > 0 && ctx.modality === 'ACCUMULATOR') {
      const note = `Gale ${level}`;
      return { intent: { direction: 'RISE' }, state: { ...st, note }, reason: `Gale ${level}: entrada na rodada` };
    }
    if (level > 0) {
      const last = st.last ?? match?.direction ?? sides.find((x): x is Side => x !== null) ?? sidesOf(ctx.modality)[0];
      const step = def.gale.plan?.[level - 1];
      if (step) {
        const base = st.entry ?? last;
        const direction = step === 'ENTRY' ? base : other(ctx.modality, base);
        const note = `Gale ${level} em ${L(direction)}`;
        return { intent: { direction }, state: { ...st, note }, reason: `Gale ${level}: ${step === 'ENTRY' ? 'lado da entrada' : 'oposto da entrada'} → ${L(direction)}` };
      }
      const mode = def.gale.direction;
      // FOLLOW: after a loss the side that came out is the other one; after an Empate nothing came out, the side stays.
      const follow = mode === 'FOLLOW' && !st.lastTied;
      const direction = mode === 'INVERT' || follow ? other(ctx.modality, last) : mode === 'SIGNAL' && match ? match.direction : last;
      const why = mode === 'INVERT' ? 'inverte' : mode === 'FOLLOW' ? (follow ? 'lado que saiu' : 'empate, repete') : mode === 'SIGNAL' && match ? describeGroup(match.rules, ctx.modality) : 'mantém';
      const note = `Gale ${level} em ${L(direction)}`;
      return { intent: { direction }, state: { ...st, note }, reason: `Gale ${level}: ${why} → ${L(direction)}` };
    }

    // 3. Waits after a chain, then the operating window.
    if (st.wait > 0 && round !== undefined) {
      st.resumeAt = round + st.wait;
      st.wait = 0;
    }
    if (st.resumeAt !== null && round !== undefined) {
      if (round < st.resumeAt) {
        const left = st.resumeAt - round;
        return idle(`Pausa: ${left} ${left === 1 ? 'rodada' : 'rodadas'} sem operar`);
      }
      st.resumeAt = null;
    }
    if (!insideSchedule(def.schedule, tick.epoch)) return idle(`Fora do horário (${def.schedule.from}–${def.schedule.to})`);

    // 4. A paper entry still open (its round not published yet): wait for it.
    if (st.vEntry) return idle('Conferindo o loss virtual');
    if (!match) return idle(def.virtualLoss.count > 0 ? `Aguardando o padrão · loss virtual ${st.vLosses} de ${def.virtualLoss.count}` : 'Aguardando o padrão');

    const signal = describeGroup(match.rules, ctx.modality);
    // 5. Loss virtual: enter on paper until the signal missed enough times in a row.
    if (def.virtualLoss.count > 0 && st.vLosses < def.virtualLoss.count) {
      if (round === undefined) return idle('Aguardando o padrão');
      st.vEntry = { round, direction: match.direction };
      return idle(`Loss virtual ${st.vLosses} de ${def.virtualLoss.count} · simulando ${ctx.modality === 'ACCUMULATOR' ? 'uma entrada' : L(match.direction)}`);
    }

    const done = def.virtualLoss.count > 0 ? `Loss virtual ${st.vLosses}/${def.virtualLoss.count} · ` : '';
    // The loss virtual count is cleared when this entry settles (onSettlement), not here: an order that
    // never went through (missed round, connection) must not cost the user the misses already counted.
    st.last = match.direction;
    st.entry = match.direction;
    const entryNote = ctx.modality === 'ACCUMULATOR' ? 'Entrada na rodada' : `Entrada em ${L(match.direction)}`;
    return { intent: { direction: match.direction }, state: { ...st, note: entryNote }, reason: `${done}${signal}` };
  },

  onSettlement: (ctx, settled) => {
    const def = ctx.params;
    // A real entry settled: the next cycle needs its own losses virtuais.
    const st: CustomState = { ...INITIAL_STATE, ...(ctx.state as Partial<CustomState>), last: settled.direction as Side, lastTied: settled.outcome === 'NEUTRAL', vLosses: 0, vEntry: null };
    if (!settled.chainOver) return { ...st, note: 'Preparando o gale' };
    if (settled.outcome === 'NEUTRAL') return { ...st, note: 'Lendo o mercado' };
    if (settled.outcome === 'WIN') return { ...st, wins: st.wins + 1, lossStreak: 0, wait: def.afterWin.waitRounds, note: 'Ciclo ganho · lendo o mercado' };
    return { ...st, lossStreak: st.lossStreak + 1, wait: def.afterLoss.waitRounds, note: 'Ciclo perdido · lendo o mercado' };
  },

  shouldEnd: (state, def) => {
    if (def.stops.lossStreak > 0 && (state.lossStreak ?? 0) >= def.stops.lossStreak) {
      return `${def.stops.lossStreak} ${def.stops.lossStreak === 1 ? 'gale perdido' : 'gales perdidos seguidos'}`;
    }
    if (def.stops.wins > 0 && (state.wins ?? 0) >= def.stops.wins) return `${def.stops.wins} ${def.stops.wins === 1 ? 'ciclo vencedor' : 'ciclos vencedores'}`;
    return null;
  },

  statusNote: (state) => state?.note ?? null,
};

// ---- Starting points for the builder ------------------------------------------

export interface CustomTemplate {
  key: string;
  name: string;
  description: string;
  modality: 'EVEN_ODD' | 'RISE_FALL' | 'ACCUMULATOR';
  def: CustomStrategyDef;
}

const T = (raw: unknown) => customStrategySchema.parse(raw);

export const CUSTOM_TEMPLATES: readonly CustomTemplate[] = [
  {
    key: 'contra-sequencia',
    name: 'Contra a sequência',
    description: '3 iguais seguidos → entra no lado oposto. Simples e frequente.',
    modality: 'EVEN_ODD',
    def: T({ rules: [{ id: 'r1', type: 'STREAK', length: 3, exact: true, entry: 'AGAINST' }] }),
  },
  {
    key: 'loss-virtual',
    name: 'Sequência + loss virtual',
    description: 'Mesmo sinal, mas só entra depois que ele errar 2 vezes no papel.',
    modality: 'EVEN_ODD',
    def: T({ rules: [{ id: 'r1', type: 'STREAK', length: 3, exact: false, entry: 'AGAINST' }], virtualLoss: { count: 2 } }),
  },
  {
    key: 'quebra',
    name: 'Segue a quebra',
    description: 'Depois de 4+ iguais, quando o lado vira, entra a favor da virada.',
    modality: 'EVEN_ODD',
    def: T({ rules: [{ id: 'r1', type: 'BREAK', minRun: 4, entry: 'FOLLOW' }], afterLoss: { waitRounds: 3 } }),
  },
  {
    key: 'padroes',
    name: 'Padrões de 4',
    description: 'Duplas e alternância: P P I I → P e P I P I → P.',
    modality: 'EVEN_ODD',
    def: T({
      rules: [
        { id: 'r1', type: 'PATTERN', sequence: ['EVEN', 'EVEN', 'ODD', 'ODD'], entry: 'EVEN' },
        { id: 'r2', type: 'PATTERN', sequence: ['ODD', 'ODD', 'EVEN', 'EVEN'], entry: 'ODD' },
        { id: 'r3', type: 'PATTERN', sequence: ['EVEN', 'ODD', 'EVEN', 'ODD'], entry: 'EVEN' },
        { id: 'r4', type: 'PATTERN', sequence: ['ODD', 'EVEN', 'ODD', 'EVEN'], entry: 'ODD' },
      ],
    }),
  },
  {
    key: 'numero-repetido',
    name: 'Número repetido',
    description: 'O mesmo número fechou 2 vezes seguidas (ex.: 7 7) → entra na paridade oposta.',
    modality: 'EVEN_ODD',
    def: T({ rules: [{ id: 'r1', type: 'REPEAT', count: 2, entry: 'AGAINST' }] }),
  },
  {
    key: 'numeros',
    name: 'Números exatos',
    description: 'Fechou no número 0 ou 5 → entra em Ímpar. Troque pelos números que quiser.',
    modality: 'EVEN_ODD',
    def: T({
      rules: [
        { id: 'r1', type: 'PATTERN', sequence: [0], entry: 'ODD' },
        { id: 'r2', type: 'PATTERN', sequence: [5], entry: 'ODD' },
      ],
    }),
  },
  {
    key: 'frequencia',
    name: 'Lado atrasado',
    description: 'Um lado saiu 14+ vezes nas últimas 20 → entra no lado que menos saiu.',
    modality: 'EVEN_ODD',
    def: T({ rules: [{ id: 'r1', type: 'FREQUENCY', window: 20, min: 14, entry: 'MINORITY' }] }),
  },
  {
    key: 'decolagem-apos-curtos',
    name: 'Rush após rodadas curtas',
    description: 'Depois de 3 velas cinzas seguidas, entra na próxima rodada e sai com +15%.',
    modality: 'ACCUMULATOR',
    // Decolagem sides: FALL = Curto (fell before the cash-out), RISE = Longo.
    def: T({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['FALL', 'FALL', 'FALL'], entry: 'RISE' }], accu: { growthRate: 0.03, cashoutTicks: 5 } }),
  },
  {
    key: 'sobe-desce-tendencia',
    name: 'Tendência Sobe/Desce',
    description: '3 velas para o mesmo lado → entra a favor, com 2 rodadas de pausa após loss.',
    modality: 'RISE_FALL',
    def: T({ rules: [{ id: 'r1', type: 'STREAK', length: 3, exact: false, entry: 'WITH' }], afterLoss: { waitRounds: 2 } }),
  },
];
